import { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";

export default function AILoader({ loading }) {
  const BASE_STAGES = useMemo(
    () => [
      { text: "Sending information to AI", icon: "✈️", animation: "send" },
      { text: "Understanding your prompt", icon: "🧠", animation: "pulse" },
      { text: "Processing visual concepts", icon: "🌀", animation: "orbit" },
      { text: "Creating image details", icon: "🎨", animation: "shimmer" },
      { text: "Finalising image", icon: "✅", animation: "success" },
    ],
    []
  );

  const LONG_WAIT_STAGE = useMemo(
    () => ({
      text: "Still generating… this is taking longer than usual",
      subtext: "Please keep this screen open. We’re finishing your result.",
      icon: "⏳",
      animation: "pulse",
    }),
    []
  );

  const USUAL_TIME_MS = 68000; // 68s
  const STAGE_STEP_MS = 10500; // RN step speed

  const [stageIndex, setStageIndex] = useState(0);
  const [longWait, setLongWait] = useState(false);

  const AI_STAGES = useMemo(() => {
    return longWait ? [...BASE_STAGES, LONG_WAIT_STAGE] : BASE_STAGES;
  }, [BASE_STAGES, LONG_WAIT_STAGE, longWait]);

  // ✅ interval + timeout refs (avoid restart bugs)
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // ✅ 65s watchdog: jump to long-wait stage and LOCK (no restart)
  useEffect(() => {
    if (!loading) {
      setLongWait(false);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setLongWait(true);
      setStageIndex(BASE_STAGES.length); // jump to long wait stage
    }, USUAL_TIME_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [loading, BASE_STAGES.length]);

  // ✅ stage progression (never loops; locks on final stage)
  useEffect(() => {
    if (!loading) {
      setStageIndex(0);
      setLongWait(false);

      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      return;
    }

    intervalRef.current = setInterval(() => {
      setStageIndex((prev) => {
        // If long wait triggered → lock to the last stage
        if (longWait) return AI_STAGES.length - 1;

        // Normal progression up to final base stage
        if (prev < BASE_STAGES.length - 1) return prev + 1;

        // Stop at "Finalising image" (don't loop)
        return prev;
      });
    }, STAGE_STEP_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loading, longWait, AI_STAGES.length, BASE_STAGES.length]);

  if (!loading) return null;

  const stage = AI_STAGES[Math.min(stageIndex, AI_STAGES.length - 1)];

  return (
    <div style={overlay}>
      <div style={card}>
        {/* Icon */}
        <div style={{ ...icon, ...animations[stage.animation] }}>
          {stage.icon}
        </div>

        {/* Text */}
        <div style={text}>{stage.text}</div>

        {/* ✅ subtext only for long wait */}
        {longWait && stage.subtext ? (
          <div style={subtext}>{stage.subtext}</div>
        ) : null}

        {/* Dots */}
        <div style={dots}>
          {AI_STAGES.map((_, i) => (
            <div
              key={i}
              style={{
                ...dot,
                background: i <= stageIndex ? "#F7941D" : "#333",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** ✅ Animations map (uses your App.css keyframes) */
const animations = {
  send: { animation: "fly 1.2s ease-in-out infinite" },
  pulse: { animation: "pulse 1.5s ease-in-out infinite" },
  orbit: { animation: "spin 1.2s linear infinite" },
  shimmer: {
    background: "linear-gradient(90deg,#F7941D,#fff,#F7941D)",
    WebkitBackgroundClip: "text",
    color: "transparent",
    animation: "shimmer 2s linear infinite",
  },
  success: { animation: "pop 0.6s ease-out infinite alternate" },
};

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.9)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
  padding: 16,
};

const card = {
  background: "#0B0F14",
  borderRadius: 18,
  border: "1px solid #2B3440",
  padding: "28px 26px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
  minWidth: 300,
};

const icon = { fontSize: 48 };

const text = {
  fontSize: 16,
  fontWeight: 800,
  color: "#E5E7EB",
  textAlign: "center",
};

const subtext = {
  fontSize: 13,
  fontWeight: 600,
  color: "#A7B0BE",
  textAlign: "center",
  lineHeight: "18px",
  marginTop: 2,
  maxWidth: 360,
};

const dots = { display: "flex", gap: 8, marginTop: 10 };

const dot = {
  width: 10,
  height: 10,
  borderRadius: 999,
  transition: "all 0.3s ease",
};
